import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "./ui/dropdown-menu";
import { UtensilsCrossed, LogOut, LayoutDashboard, User, Globe, Check, ShieldCheck } from "lucide-react";
import { SocialLinks } from "./SocialLinks";

const LanguageSwitcher = () => {
  const { lang, setLang, languages } = useLang();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="language-switcher"
          className="flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-bold text-[#2B2B2B] hover:bg-[#F4EFE6] transition-colors"
        >
          <Globe className="w-4 h-4" />
          <span className="uppercase font-mono">{lang}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-white border-[#E5E0D8]">
        {languages.map((l) => (
          <DropdownMenuItem
            key={l.code}
            data-testid={`lang-option-${l.code}`}
            onClick={() => setLang(l.code)}
            className="flex items-center justify-between gap-4 cursor-pointer font-medium"
          >
            {l.label}
            {lang === l.code && <Check className="w-4 h-4 text-[#E63946]" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const Header = () => {
  const { customer, owner, logoutCustomer, logoutOwner } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <header
      data-testid="app-header"
      className="sticky top-0 z-50 bg-[#FDFBF7]/90 backdrop-blur-xl border-b border-[#E5E0D8]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2">
          <span className="w-9 h-9 rounded-full bg-[#E63946] flex items-center justify-center">
            <UtensilsCrossed className="w-5 h-5 text-white" />
          </span>
          <span className="text-2xl font-display font-black tracking-tighter text-[#2B2B2B]">
            Vytali
          </span>
        </Link>

        <nav className="flex items-center gap-2 md:gap-3">
          <button
            data-testid="header-partners-link"
            onClick={() => navigate("/partenaires")}
            className="hidden md:flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold text-[#2B2B2B] hover:bg-[#F4EFE6] transition-colors"
          >
            {t("header.partners")}
          </button>
          <button
            data-testid="header-membership-link"
            onClick={() => navigate("/membre")}
            className="hidden sm:flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold text-[#2B2B2B] hover:bg-[#F4EFE6] transition-colors"
          >
            {t("header.membership")}
          </button>
          <div className="hidden lg:flex items-center">
            <SocialLinks variant="header" />
          </div>
          <LanguageSwitcher />
          {owner ? (
            <>
              <button
                data-testid="header-dashboard-btn"
                onClick={() => navigate("/dashboard")}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-white border-2 border-[#2B2B2B] text-[#2B2B2B] font-bold text-sm hover:bg-[#F4EFE6] transition-colors"
              >
                <LayoutDashboard className="w-4 h-4" /> {t("header.dashboard")}
              </button>
              <button
                data-testid="owner-logout-btn"
                onClick={() => { logoutOwner(); navigate("/"); }}
                className="p-2 rounded-full hover:bg-[#F4EFE6] transition-colors"
                title={t("header.logout")}
              >
                <LogOut className="w-5 h-5 text-[#5C5C5C]" />
              </button>
            </>
          ) : customer ? (
            <>
              {(customer.role === "super_admin" || customer.role === "staff") && (
                <button
                  data-testid="header-admin-btn"
                  onClick={() => navigate("/admin")}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#2B2B2B] text-white font-bold text-sm hover:bg-[#1a1a1a] transition-colors"
                >
                  <ShieldCheck className="w-4 h-4" /> Admin
                </button>
              )}
              <div className="flex items-center gap-2 pr-1">
                {customer.picture ? (
                  <img src={customer.picture} alt={customer.name} className="w-8 h-8 rounded-full object-cover" />
                ) : (
                  <span className="w-8 h-8 rounded-full bg-[#F4A261] flex items-center justify-center">
                    <User className="w-4 h-4 text-white" />
                  </span>
                )}
                <span data-testid="customer-name" className="text-sm font-medium hidden sm:block">{customer.name?.split(" ")[0]}</span>
              </div>
              <button
                data-testid="customer-logout-btn"
                onClick={() => { logoutCustomer(); navigate("/"); }}
                className="p-2 rounded-full hover:bg-[#F4EFE6] transition-colors"
                title={t("header.logout")}
              >
                <LogOut className="w-5 h-5 text-[#5C5C5C]" />
              </button>
            </>
          ) : (
            <>
              {location.pathname !== "/login" && (
                <button
                  data-testid="header-customer-login-btn"
                  onClick={() => navigate("/login")}
                  className="px-4 py-2 rounded-full text-[#2B2B2B] font-bold text-sm hover:bg-[#F4EFE6] transition-colors"
                >
                  {t("header.login")}
                </button>
              )}
              <button
                data-testid="header-restaurant-cta"
                onClick={() => navigate("/restaurant")}
                className="px-5 py-2.5 rounded-full bg-[#E63946] text-white font-bold text-sm hover:scale-[1.03] transition-transform"
              >
                {t("header.restaurantSpace")}
              </button>
            </>
          )}
        </nav>
      </div>
    </header>
  );
};
